import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  compareModeState,
  setFirstCompareModel,
  setSecondCompareModel,
  useAppDispatch,
  useAppSelector,
} from 'src/store';
import {
  useConversationParams,
  useMessageListRefActions,
  useQueryParams,
} from 'src/hooks';
import {
  FIRST_COMPARING_CONV_PARAM,
  SECOND_COMPARE_MODEL_PARAM,
} from 'src/constants';
import { AppRoutes } from 'src/types';

export const useCompareMode = () => {
  const dispatch = useAppDispatch();

  const location = useLocation();
  const navigate = useNavigate();
  const { setSearchParams, removeMultipleSearchParams } = useQueryParams();
  const { isLandingPage } = useConversationParams();

  const { resetMessageListRefCache } = useMessageListRefActions();

  const { firstCompareModel, secondCompareModel } =
    useAppSelector(compareModeState);

  const changeFirstCompareModel = useCallback(
    (value?: string) => {
      dispatch(setFirstCompareModel(value));
    },
    [dispatch],
  );

  const changeSecondCompareModel = useCallback(
    (value?: string) => {
      dispatch(setSecondCompareModel(value));
    },
    [dispatch],
  );

  const setCompareMode = useCallback(() => {
    if (location.pathname !== AppRoutes.HOME || !isLandingPage) {
      navigate(`${AppRoutes.HOME}?compare=true`);
    } else {
      setSearchParams({ compare: 'true' }, { replace: true });
    }
  }, [setSearchParams, navigate, location, isLandingPage]);

  const resetCompareMode = useCallback(() => {
    removeMultipleSearchParams([
      'compare',
      FIRST_COMPARING_CONV_PARAM,
      SECOND_COMPARE_MODEL_PARAM,
    ]);

    resetMessageListRefCache();
  }, [removeMultipleSearchParams, resetMessageListRefCache]);

  return useMemo(
    () => ({
      firstCompareModel,
      secondCompareModel,
      setCompareMode,
      resetCompareMode,
      changeFirstCompareModel,
      changeSecondCompareModel,
    }),
    [
      firstCompareModel,
      secondCompareModel,
      setCompareMode,
      resetCompareMode,
      changeFirstCompareModel,
      changeSecondCompareModel,
    ],
  );
};
